import {
  faFolderOpen,
  faMountain,
  faPalette,
} from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "urql";

import { Workspace } from "@/components/graphql/schema.graphql";
import { getForgeSessionUrl } from "@/components/inference/utils";
import { Icon, Menu, MenuItem, useSnackbar } from "@/components/ui-v2";
import { useStyleActions, useUrqlResponseHandler } from "@/hooks";

import { CreateAsset } from "../../asset/queries.graphql";
import { useDrive } from "../DriveContext";
import { DriveNewDirectoryDialog } from "./DriveNewDirectoryDialog";

type Anchor = {
  position: {
    top: number;
    left: number;
  };
};

type DriveBodyContextMenuProps = {
  workspace: Pick<Workspace, "id" | "name">;
  anchor?: Anchor;
  onClose: () => void;
};

export const DriveBodyContextMenu = ({
  anchor,
  workspace,
  onClose,
}: DriveBodyContextMenuProps) => {
  const { currentDirectoryPath: directory } = useDrive();
  const [showNewDirectoryDialog, setShowNewDirectoryDialog] = useState(false);
  const { showError } = useSnackbar();
  const navigate = useNavigate();
  const { getResponseFromState } = useUrqlResponseHandler();
  const {
    createStyle: { execute: createStyle },
  } = useStyleActions();
  const [, createAsset] = useMutation(CreateAsset);

  const onClickNewDirectory = () => {
    setShowNewDirectoryDialog(true);
    onClose();
  };

  const onClickNewStyle = async () => {
    const { style } = await createStyle({
      workspaceId: workspace.id,
      directory,
      trainingConfig: {
        useImageCaptions: true,
      },
    });

    if (style?.slug) {
      navigate(`/${workspace.name}/models/${style.slug}/create`);
    }

    onClose();
  };

  const onClickNewAsset = async () => {
    const response = await createAsset({
      createAssetInput: {
        workspaceId: workspace.id,
        directory,
      },
    });

    const {
      error,
      data: { createAsset: createAssetResult },
    } = getResponseFromState({
      state: response,
      errorMessage: "There was an error creating your Asset.",
    });

    if (error || !createAssetResult?.sessionId) {
      showError("There was an error creating your Asset.");
      return;
    }

    navigate(
      getForgeSessionUrl({
        workspaceName: workspace.name,
        sessionId: createAssetResult.sessionId,
        assetId: createAssetResult.id,
      }),
    );
    onClose();
  };

  return (
    <>
      <Menu
        open={!!anchor}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={anchor?.position}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "180px",
          },
        }}
      >
        <MenuItem
          onClick={onClickNewDirectory}
          icon={<Icon icon={faFolderOpen} />}
        >
          New Directory
        </MenuItem>
        <MenuItem onClick={onClickNewStyle} icon={<Icon icon={faPalette} />}>
          New Model
        </MenuItem>
        <MenuItem onClick={onClickNewAsset} icon={<Icon icon={faMountain} />}>
          New Asset
        </MenuItem>
      </Menu>
      <DriveNewDirectoryDialog
        isOpen={showNewDirectoryDialog}
        onClose={() => setShowNewDirectoryDialog(false)}
        workspaceId={workspace.id}
      />
    </>
  );
};
